import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Gift, PartyPopper, Instagram } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { SectionHead, EASE } from "./Motion";

const input = "mt-1.5 w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-3 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/70 focus:shadow-[0_0_0_3px_rgba(200,164,93,0.12)] transition-[border-color,box-shadow] duration-300 placeholder:text-[#857078]/50";

export default function ContestSection() {
  const { settings } = useStore();
  const [form, setForm] = useState({ name: "", email: "", instagram: "", address: "", pincode: "" });
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (settings && settings.contest_enabled === false) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/contest", { ...form, consent });
      setDone(true);
      api.post("/events", { type: "contest_entry", meta: { pincode: form.pincode } }).catch(() => {});
    } catch (err) {
      setError(formatApiError(err));
    }
    setLoading(false);
  };

  return (
    <section className="py-24" data-testid="contest-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="relative card-surface rounded-3xl p-6 sm:p-12 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#7E1E3F]/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-16 w-56 h-56 rounded-full bg-[#C9A24B]/15 blur-3xl pointer-events-none" />

          <AnimatePresence mode="wait">
            {done ? (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: EASE }} className="text-center py-10" data-testid="contest-success">
                <motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.15 }}
                  className="w-16 h-16 mx-auto rounded-full bg-[#7E1E3F]/15 border border-[#7E1E3F]/40 flex items-center justify-center">
                  <PartyPopper className="text-[#7E1E3F]" size={28} />
                </motion.div>
                <h3 className="font-serif-lux text-3xl text-[#2E1A22] mt-6">Entry Submitted Successfully!</h3>
                <p className="text-sm sm:text-base text-[#857078] mt-4 max-w-md mx-auto leading-relaxed">
                  Thank you for participating in the Savyron Contest. Your entry has been received successfully.
                  We'll contact the winner through the details provided.
                </p>
                <p className="font-mono-lux text-[11px] tracking-[0.25em] uppercase text-[#7E1E3F] mt-6">Good luck from the Savyron family</p>
              </motion.div>
            ) : (
              <motion.div key="form" exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }}>
                <div className="text-center mb-8">
                  <motion.div initial={{ scale: 0.8, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }} transition={{ duration: 0.6, ease: EASE }}
                    className="w-14 h-14 mx-auto rounded-full bg-[#7E1E3F]/12 border border-[#7E1E3F]/35 flex items-center justify-center mb-5">
                    <Gift className="text-[#7E1E3F]" size={24} />
                  </motion.div>
                  <SectionHead overline="The Savyron Contest" title={settings?.contest_title || "Participate to Win Exciting Prizes!"}
                    sub={settings?.contest_subtitle || "Enter the Savyron contest for a chance to win exciting prizes. Fill in your details below to participate."}
                    center testid="contest-head" />
                  {settings?.contest_prize_note && (
                    <p className="text-sm text-[#7E1E3F] font-medium -mt-6 mb-4" data-testid="contest-prize-note">{settings.contest_prize_note}</p>
                  )}
                  {settings?.contest_deadline && (
                    <p className="text-xs font-mono-lux tracking-[0.2em] uppercase text-[#857078] -mt-2 mb-4" data-testid="contest-deadline">{settings.contest_deadline}</p>
                  )}
                  {settings?.contest_rules && (
                    <div className="text-left max-w-md mx-auto mb-6 bg-[#FBF7F1] border border-[#E5E7EB] rounded-xl px-5 py-4" data-testid="contest-rules">
                      <p className="text-[10px] font-mono-lux tracking-[0.2em] uppercase text-[#7E1E3F] mb-2">How to Participate</p>
                      {settings.contest_rules.split("\n").filter(Boolean).map((r, i) => (
                        <p key={i} className="text-xs text-[#857078] leading-relaxed">— {r}</p>
                      ))}
                    </div>
                  )}
                </div>
                <form onSubmit={submit} className="space-y-5" data-testid="contest-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <label className="block">
                      <span className="text-xs font-mono-lux tracking-[0.15em] uppercase text-[#857078]">Full Name *</span>
                      <input required minLength={2} className={input} value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" data-testid="contest-name" />
                    </label>
                    <label className="block">
                      <span className="text-xs font-mono-lux tracking-[0.15em] uppercase text-[#857078]">Email Address *</span>
                      <input required type="email" className={input} value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="contest-email" />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-xs font-mono-lux tracking-[0.15em] uppercase text-[#857078]">Instagram ID *</span>
                    <div className="relative">
                      <Instagram size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7E1E3F]" />
                      <input required className={`${input} pl-10`} value={form.instagram}
                        onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="@yourusername" data-testid="contest-instagram" />
                    </div>
                  </label>
                  <label className="block">
                    <span className="text-xs font-mono-lux tracking-[0.15em] uppercase text-[#857078]">Complete Address *</span>
                    <textarea required rows={3} className={input} value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="House/flat, street, city, state" data-testid="contest-address" />
                  </label>
                  <label className="block sm:w-1/2">
                    <span className="text-xs font-mono-lux tracking-[0.15em] uppercase text-[#857078]">Pincode *</span>
                    <input required inputMode="numeric" maxLength={6} className={input} value={form.pincode}
                      onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="6-digit pincode" data-testid="contest-pincode" />
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer pt-1">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                      className="accent-[#7E1E3F] w-4 h-4 mt-0.5" data-testid="contest-consent" />
                    <span className="text-xs text-[#857078] leading-relaxed">
                      I agree to share my details with Savyron for this contest and to be contacted about the result. My information will be stored securely and never sold.
                    </span>
                  </label>
                  {error && <p className="text-sm text-[#E63946]" data-testid="contest-error">{error}</p>}
                  <motion.button type="submit" disabled={loading} whileTap={{ scale: 0.98 }}
                    className="w-full bg-[#7E1E3F] text-white font-semibold text-sm tracking-[0.2em] uppercase rounded-full py-4 hover:bg-[#5C1230] transition-colors duration-300 disabled:opacity-60 shadow-[0_10px_30px_rgba(200,164,93,0.3)]"
                    data-testid="contest-submit">
                    {loading ? "Submitting…" : "Participate Now"}
                  </motion.button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
